import get from 'lodash.get';

import * as Constants from './constants';
import { OtherShippingOptions } from './other-options-list';
import OptionsCodes from '../../constants/other-shipping-options-constants';
import { emptyArray } from 'ui-components/default-props/empty';
import { REGULATED_CONTENT_TYPE_NONE_VALUE } from '../../orders/other-shipping-options/components/inputs/regulated-content-type-dropdown/regulated-content-types';

const nonModalOptions = [Constants.confirmation, Constants.confirmationEnumId];
const hasValue = (val) => val !== undefined && val !== null;

const keepOptionNameBasedOnCriteria = (optionName, filterParams) => {
  const { fromCountryCode, isInternational } = filterParams;
  if (optionName === Constants.b13ACanada) {
    return fromCountryCode === 'CA' && isInternational;
  }
  return true;
};

class OtherShippingOptionsServiceClass {
  #initialState = Constants.initialOptionState();

  getInitialStatePropertyById(id) {
    return this.#initialState[id];
  }

  addFulfillmentPropertiesToState(properties) {
    this.#initialState = {
      ...this.#initialState,
      ...Constants.initialOptionState(properties),
    };
  }

  buildOptions(optionCodes, enabledOptions, matchedCountryCode = 'US', sellerDefaultCurrency = 'USD', fulfillmentProperties = {}) {
    // TODO: filter out include return label if seller has no services that support returns to the given recipient address
    const localOptionCodes = [...optionCodes];
    const { recipientInfo } = fulfillmentProperties;
    this.addFulfillmentPropertiesToState(recipientInfo);
    const options = {};
    // build initial object
    localOptionCodes.forEach((option) => {
      const optionName = Constants.otherOptionMap[option];
      if (!optionName) {
        return;
      }

      // filter options that are enabled for service but under certain conditions (like fromCountryCode)
      const { properties } = fulfillmentProperties;

      if (
        !keepOptionNameBasedOnCriteria(optionName, {
          fromCountryCode: matchedCountryCode,
          isInternational: properties?.isInternational || false,
        })
      ) {
        return;
      }

      options[optionName] = Constants.initialOptionStateKeys.includes(optionName)
        ? {
            value: this.getInitialStatePropertyById(optionName),
            checked: false,
          }
        : {
            value: false,
            checked: false,
          };
    });

    if (options.billToAccount) {
      options.billToAccount.billingCountry = matchedCountryCode;
    }

    if (options.serviceLevel) {
      options.serviceLevel = {
        value: Constants.initialOptionState().serviceLevel,
        checked: false,
      };
    }
    // if these options are present, provide data structure
    Object.keys(enabledOptions).forEach((option) => {
      if (nonModalOptions.includes(option)) {
        return;
      }

      if (!hasValue(enabledOptions[option]) || !hasValue(options[option])) {
        return;
      }
      if (option === Constants.freightClass || option === Constants.b13ACanada) {
        options[option] = {
          value: enabledOptions[option],
          checked: true,
        };
        return;
      }

      if (option === Constants.consequentialLoss) {
        const codValue = enabledOptions[option];
        options[option] = {
          value: {
            ...codValue,
            payment: {
              ...codValue.payment,
              code: sellerDefaultCurrency,
            },
          },
          checked: true,
        };
        return;
      }

      // Handle dropdown options where empty string, null, or 'None' means "not selected"
      if (option === Constants.regulatedContentType) {
        const value = enabledOptions[option];
        const isNoneValue = !value || value === REGULATED_CONTENT_TYPE_NONE_VALUE;
        options[option] = {
          value: isNoneValue ? null : value,
          checked: !isNoneValue,
        };
        return;
      }

      options[option] = {
        value: enabledOptions[option],
        checked: true,
      };
    });
    if (!localOptionCodes.includes(OptionsCodes.billToThirdParty)) {
      options.billToAccount = {
        value: null,
        checked: false,
        changed: false,
      };
    }

    return options;
  }

  translateDataForSaving(options) {
    const cleanedOptions = {};
    Object.keys(options).forEach((option) => {
      const toClean = options[option];
      if (option === Constants.includeReturnLabel && toClean.value) {
        delete toClean.value.packageId;
      }
      cleanedOptions[option] = toClean.value;
    });
    return cleanedOptions;
  }

  getOtherShippingOptions(sectionsToShow) {
    const options = {};
    Constants.allCategories.forEach((categoryName) => {
      options[categoryName] = [];
    });
    sectionsToShow.forEach((category) => {
      options[category] = OtherShippingOptions.filter((option) => option.category === category);
    });
    return options;
  }

  getAbbreviatedName(optionId) {
    const option = OtherShippingOptions.find((optn) => optn.optionName === optionId);
    if (!option) {
      return null;
    }
    return option.abbreviatedName;
  }

  getNonModalOptions() {
    return nonModalOptions;
  }

  cleanOptionsAfterServiceChange(optionCodes, currentOptions) {
    const cleanOptions = {};
    // set non-carrier dependent options
    this.getNonModalOptions().forEach((option) => (cleanOptions[option] = currentOptions[option]));
    optionCodes.forEach((option) => {
      const optionName = Constants.otherOptionMap[option];
      if (optionName && currentOptions[optionName]) {
        cleanOptions[optionName] = currentOptions[optionName];
      }
    });
    return cleanOptions;
  }

  convertEnabledOptionsToAbbreviatedNameArray(options) {
    const optionNames = [];
    if (!options) {
      return [];
    }
    Object.keys(options).forEach((optionName) => {
      if (options[optionName] && options[optionName] !== 'None') {
        const abbreviatedName = this.getAbbreviatedName(optionName);
        if (abbreviatedName !== null) {
          optionNames.push(abbreviatedName);
        }
      }
    });
    return optionNames;
  }

  getDefaultOptions() {
    return Constants.ALL_DEFAULT_OPTIONS;
  }

  getConfirmationTextForCarrier(carrier, confirmationEnumId) {
    const carrierConfirmations = carrier?.confirmations || emptyArray;
    const selectedConfirmation = carrierConfirmations.find((type) => parseInt(type.confirmationId, 10) === confirmationEnumId);

    return selectedConfirmation?.name;
  }

  getConfirmationText(shipment, carriers, selectedService) {
    const carrierId = get(selectedService, 'carrierId');
    const selectedCarrier = carriers.find((carrier) => carrier.carrierId === carrierId);
    const shipmentConfirmationTypeEnumId = get(shipment, 'options.confirmationEnumId');

    return this.getConfirmationTextForCarrier(selectedCarrier, shipmentConfirmationTypeEnumId);
  }

  getInitialStateForOption(id, sellerDefaultCurrency) {
    const option = this.getInitialStatePropertyById(id);
    if (id === Constants.collectOnDelivery) {
      option.payment.code = sellerDefaultCurrency;
    }
    return option;
  }
}

export const OtherShippingOptionsService = new OtherShippingOptionsServiceClass();
